/**
 * 工作区右下角：页面布局模式 + 缩放菜单。
 * 定位由 CanvasDesignerOverlays 负责。
 */

import type { Key } from 'react';
import { Button, Dropdown, Kbd, Label, ToggleButton, ToggleButtonGroup, cn } from '@heroui/react';
import {
  IconColumns1,
  IconLayoutColumns,
  IconLayoutRows,
} from '@tabler/icons-react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import { WORKSPACE_ZOOM_MIN, WORKSPACE_ZOOM_MAX } from '@/components/canvas/shared/constants/legacyConfig';
import type { WorkspacePageLayoutMode } from '@/components/canvas/document/publicTypes';

function isMacOs(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || navigator.platform || '');
}

function clampZoom(value: number): number {
  return Math.max(WORKSPACE_ZOOM_MIN, Math.min(WORKSPACE_ZOOM_MAX, value));
}

function selectionToSingleKey(keys: 'all' | Set<Key>): WorkspacePageLayoutMode | undefined {
  if (keys === 'all') return undefined;
  const first = keys.values().next().value;
  if (first === 'single' || first === 'horizontal' || first === 'vertical') return first;
  return undefined;
}

export interface BottomRightToolBarProps {
  contentSize: { width: number; height: number };
  onFitToScreenRequest?: () => void;
  className?: string;
}

export function BottomRightToolBar({
  contentSize,
  onFitToScreenRequest,
  className,
}: BottomRightToolBarProps) {
  const { zoom, setZoom, fitToScreen, workspacePageLayoutMode, setWorkspacePageLayoutMode } =
    useCanvas();
  const percent = Math.round(zoom * 100);
  const mac = isMacOs();

  const runZoomIn = () => {
    const z = useCanvasStore.getState().zoom;
    setZoom(clampZoom(z * 1.1));
  };
  const runZoomOut = () => {
    const z = useCanvasStore.getState().zoom;
    setZoom(clampZoom(z / 1.1));
  };
  const runZoom100 = () => setZoom(1);
  const runZoomFit = () => {
    if (onFitToScreenRequest) onFitToScreenRequest();
    else if (contentSize.width > 0 && contentSize.height > 0) {
      fitToScreen(contentSize.width, contentSize.height);
    }
  };

  return (
    <div className={cn('pointer-events-auto flex items-center gap-2', className)}>
      <ToggleButtonGroup
        aria-label="工作区页面布局"
        selectionMode="single"
        selectedKeys={new Set([workspacePageLayoutMode])}
        onSelectionChange={(keys) => {
          const next = selectionToSingleKey(keys);
          if (next) setWorkspacePageLayoutMode(next);
        }}
        size="sm"
      >
        <ToggleButton id="single" isIconOnly aria-label="单页模式">
          <IconColumns1 className="h-4 w-4" stroke={1.75} />
        </ToggleButton>
        <ToggleButton id="horizontal" isIconOnly aria-label="横向排列">
          <ToggleButtonGroup.Separator />
          <IconLayoutColumns className="h-4 w-4" stroke={1.75} />
        </ToggleButton>
        <ToggleButton id="vertical" isIconOnly aria-label="纵向排列">
          <ToggleButtonGroup.Separator />
          <IconLayoutRows className="h-4 w-4" stroke={1.75} />
        </ToggleButton>
      </ToggleButtonGroup>

      <Dropdown>
        <Button variant="tertiary" size="sm" className="min-w-[4.25rem] tabular-nums" aria-label="画布缩放">
          {percent}%
        </Button>
        <Dropdown.Popover className="min-w-[240px]" placement="top end" offset={8}>
          <Dropdown.Menu
            aria-label="缩放选项"
            onAction={(key) => {
              if (key === 'zoom-in') runZoomIn();
              else if (key === 'zoom-out') runZoomOut();
              else if (key === 'zoom-100') runZoom100();
              else if (key === 'zoom-fit') runZoomFit();
            }}
          >
            <Dropdown.Item id="zoom-in" textValue="放大">
              <div className="flex w-full min-w-0 items-center justify-between gap-4">
                <Label className="font-normal">放大</Label>
                <span className="flex shrink-0 items-center gap-0.5">
                  <Kbd>{mac ? '⌘' : 'Ctrl'}</Kbd>
                  <Kbd>+</Kbd>
                </span>
              </div>
            </Dropdown.Item>
            <Dropdown.Item id="zoom-out" textValue="缩小">
              <div className="flex w-full min-w-0 items-center justify-between gap-4">
                <Label className="font-normal">缩小</Label>
                <span className="flex shrink-0 items-center gap-0.5">
                  <Kbd>{mac ? '⌘' : 'Ctrl'}</Kbd>
                  <Kbd>−</Kbd>
                </span>
              </div>
            </Dropdown.Item>
            <Dropdown.Item id="zoom-100" textValue="100% 显示">
              <div className="flex w-full min-w-0 items-center justify-between gap-4">
                <Label className="font-normal">100% 显示</Label>
                <span className="flex shrink-0 items-center gap-0.5">
                  <Kbd>{mac ? '⇧' : 'Shift'}</Kbd>
                  <Kbd>0</Kbd>
                </span>
              </div>
            </Dropdown.Item>
            <Dropdown.Item id="zoom-fit" textValue="适应当前页">
              <div className="flex w-full min-w-0 items-center justify-between gap-4">
                <Label className="font-normal">适应当前页</Label>
                <span className="flex shrink-0 items-center gap-0.5">
                  <Kbd>{mac ? '⇧' : 'Shift'}</Kbd>
                  <Kbd>1</Kbd>
                </span>
              </div>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </div>
  );
}
